import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationsApi } from '../api/organizations';
import { usersApi } from '../api/users';
import { s3Api } from '../api/s3';
import DashboardLayout from '../components/DashboardLayout';
import './DashboardPages.css';

function ImageUploadModal({ isOpen, onClose, onSave }) {
	const [activeTab, setActiveTab] = useState('upload');
	const [imageUrl, setImageUrl] = useState('');
	const [selectedFile, setSelectedFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);

	const handleUrlChange = (e) => {
		setImageUrl(e.target.value);
		setSelectedFile(null);
		setPreviewUrl(e.target.value);
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setSelectedFile(file);
			setImageUrl('');
			setPreviewUrl(URL.createObjectURL(file));
		}
	};

	const handleSave = () => {
		if (selectedFile) {
			onSave(selectedFile);
		} else if (imageUrl) {
			onSave(imageUrl);
		}
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className='announcement-modal-overlay'>
			<div className='announcement-modal image-upload-modal'>
				<div className='image-upload-tabs'>
					<button
						className={`image-upload-tab ${
							activeTab === 'upload' ? 'active' : ''
						}`}
						onClick={() => setActiveTab('upload')}>
						Upload File
					</button>
					<button
						className={`image-upload-tab ${
							activeTab === 'url' ? 'active' : ''
						}`}
						onClick={() => setActiveTab('url')}>
						Image URL
					</button>
				</div>

				{activeTab === 'upload' ? (
					<input
						type='file'
						accept='image/*'
						onChange={handleFileChange}
						className='form-input'
					/>
				) : (
					<input
						type='url'
						value={imageUrl}
						onChange={handleUrlChange}
						placeholder='Enter image URL'
						className='form-input'
					/>
				)}

				{previewUrl && (
					<div className='image-preview-container'>
						<img
							src={previewUrl}
							alt='Preview'
							className='org-preview-image'
						/>
					</div>
				)}

				<div className='modal-actions'>
					<button
						className='modal-button-secondary'
						onClick={onClose}>
						Cancel
					</button>
					<button
						className='modal-button-primary'
						onClick={handleSave}
						disabled={!previewUrl}>
						Save
					</button>
				</div>
			</div>
		</div>
	);
}

function DashboardAdminOrganizationCreate() {
	const navigate = useNavigate();
	const [error, setError] = useState('');
	const [formData, setFormData] = useState({
		name: '',
		addressOne: '',
		addressTwo: '',
		city: '',
		state: '',
		zipCode: '',
		phoneNumber: '',
		website: '',
		facebook: '',
		x: '',
		instagram: '',
		primaryColor: '#ffffff',
		secondaryColor: '#ffffff',
		accentColor: '#ffffff',
		orgPicture: null,
		coverImage: null,

		seniorPastorId: null,
	});
	const [isImageModalOpen, setIsImageModalOpen] = useState(false);
	const [activeImageEdit, setActiveImageEdit] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const [selectedAdmin, setSelectedAdmin] = useState(null);

	const handleUserSearch = async (email) => {
		try {
			const data = await usersApi.usersByEmail.search(email);
			setSearchResults(data.users);
		} catch (error) {
			console.error('Error searching users:', error);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			// First, handle file uploads if they exist
			let orgPictureUrl = formData.orgPicture;
			let coverImageUrl = formData.coverImage;

			if (formData.orgPicture instanceof File) {
				// Use s3Api to upload the organization picture
				// Using the correct API endpoint path
				orgPictureUrl = await s3Api.upload(
					'new',
					formData.orgPicture,
					'org-pictures'
				);
			}

			if (formData.coverImage instanceof File) {
				// Use s3Api to upload the cover image
				coverImageUrl = await s3Api.upload(
					'new',
					formData.coverImage,
					'cover-images'
				);
			}

			const apiData = {
				...formData,
				orgPicture: orgPictureUrl,
				coverImage: coverImageUrl,
				seniorPastorId: selectedAdmin?.id || null, // Make sure we include the selected admin
			};

			setError('');
			await organizationsApi.create(apiData);
			navigate('/dashboard/admin');
		} catch (err) {
			setError(
				'Failed to create organization: ' +
					(err.response?.data?.message || err.message)
			);
			console.error(err);
		}
	};

	const handleImageSave = (imageData) => {
		setFormData({
			...formData,
			[activeImageEdit]: imageData,
		});
	};

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<h1>Create New Organization</h1>
				<div className='content-section'>
					{error && <div className='error-message'>{error}</div>}

					<div
						className='org-cover-container'
						onClick={() => {
							setActiveImageEdit('coverImage');
							setIsImageModalOpen(true);
						}}>
						{formData.coverImage ? (
							<img
								src={
									formData.coverImage instanceof File
										? URL.createObjectURL(
												formData.coverImage
										  )
										: formData.coverImage
								}
								alt='Organization Cover'
								className='org-cover-image'
							/>
						) : (
							<div className='org-cover-placeholder'>
								<span className='material-icons'>image</span>
								<span>Click to add cover image</span>
							</div>
						)}

						<div
							className='org-avatar-container'
							onClick={(e) => {
								e.stopPropagation();
								setActiveImageEdit('orgPicture');
								setIsImageModalOpen(true);
							}}>
							{formData.orgPicture ? (
								<img
									src={
										formData.orgPicture instanceof File
											? URL.createObjectURL(
													formData.orgPicture
											  )
											: formData.orgPicture
									}
									alt='Organization'
									className='org-avatar'
								/>
							) : (
								<div className='org-avatar-placeholder'>
									<span className='material-icons'>
										business
									</span>
								</div>
							)}
						</div>
					</div>

					<ImageUploadModal
						isOpen={isImageModalOpen}
						onClose={() => setIsImageModalOpen(false)}
						onSave={handleImageSave}
						shouldCrop={true}
					/>

					<form
						className='org-form'
						onSubmit={handleSubmit}>
						<div className='form-group'>
							<label>Organization Name</label>
							<input
								type='text'
								placeholder='Enter organization name'
								className='form-input'
								value={formData.name}
								onChange={(e) =>
									setFormData({
										...formData,
										name: e.target.value,
									})
								}
								required
							/>
						</div>

						<div className='form-group'>
							<label>Organization Admin</label>
							<div className='admin-search-container'>
								<input
									type='text'
									placeholder='Search user by email'
									className='form-input'
									value={searchTerm}
									onChange={(e) => {
										setSearchTerm(e.target.value);
										if (e.target.value.length >= 3) {
											handleUserSearch(e.target.value);
										} else {
											setSearchResults([]);
										}
									}}
								/>
								{searchResults.length > 0 && !selectedAdmin && (
									<div className='search-results'>
										{searchResults.map((user) => (
											<div
												key={user.id}
												className='search-result-item'
												onClick={() => {
													setSelectedAdmin(user);
													setSearchTerm(
														`${user.firstName} ${user.lastName} (${user.email})`
													);
													setSearchResults([]);
												}}>
												{user.firstName} {user.lastName}{' '}
												- {user.email}
											</div>
										))}
									</div>
								)}
							</div>
							{selectedAdmin && (
								<div className='selected-admin'>
									Selected: {selectedAdmin.firstName}{' '}
									{selectedAdmin.lastName}
									<button
										type='button'
										className='clear-admin'
										onClick={() => {
											setSelectedAdmin(null);
											setSearchTerm('');
										}}>
										×
									</button>
								</div>
							)}
						</div>

						<div className='form-group'>
							<label>Address Line 1</label>
							<input
								type='text'
								placeholder='Street address'
								className='form-input'
								value={formData.addressOne}
								onChange={(e) =>
									setFormData({
										...formData,
										addressOne: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Address Line 2</label>
							<input
								type='text'
								placeholder='Apt, Suite, etc. (optional)'
								className='form-input'
								value={formData.addressTwo}
								onChange={(e) =>
									setFormData({
										...formData,
										addressTwo: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-row'>
							<div className='form-group'>
								<label>City</label>
								<input
									type='text'
									placeholder='City'
									className='form-input'
									value={formData.city}
									onChange={(e) =>
										setFormData({
											...formData,
											city: e.target.value,
										})
									}
								/>
							</div>
							<div className='form-group'>
								<label>State</label>
								<input
									type='text'
									placeholder='State'
									className='form-input'
									value={formData.state}
									onChange={(e) =>
										setFormData({
											...formData,
											state: e.target.value,
										})
									}
								/>
							</div>
							<div className='form-group'>
								<label>ZIP Code</label>
								<input
									type='text'
									placeholder='ZIP Code'
									className='form-input'
									value={formData.zipCode}
									onChange={(e) =>
										setFormData({
											...formData,
											zipCode: e.target.value,
										})
									}
								/>
							</div>
						</div>

						<div className='form-group'>
							<label>Phone Number</label>
							<input
								type='tel'
								placeholder='Phone number'
								className='form-input'
								value={formData.phoneNumber}
								onChange={(e) =>
									setFormData({
										...formData,
										phoneNumber: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Website</label>
							<input
								type='url'
								placeholder='Website URL'
								className='form-input'
								value={formData.website}
								onChange={(e) =>
									setFormData({
										...formData,
										website: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Primary Color</label>
							<input
								type='color'
								className='form-input color-input'
								value={formData.primaryColor}
								onChange={(e) =>
									setFormData({
										...formData,
										primaryColor: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Secondary Color</label>
							<input
								type='color'
								className='form-input color-input'
								value={formData.secondaryColor}
								onChange={(e) =>
									setFormData({
										...formData,
										secondaryColor: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Accent Color</label>
							<input
								type='color'
								className='form-input color-input'
								value={formData.accentColor}
								onChange={(e) =>
									setFormData({
										...formData,
										accentColor: e.target.value,
									})
								}
							/>
						</div>

						<div className='submit-container'>
							<button
								type='submit'
								className='submit-btn'>
								Create Organization
							</button>
						</div>
					</form>
				</div>
			</div>
		</DashboardLayout>
	);
}

export default DashboardAdminOrganizationCreate;
